import { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer } from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || 'https://<api-id>.execute-api.us-east-1.amazonaws.com/prod';

// Load Google's <model-viewer> web component once
if (!document.querySelector('script[src*="model-viewer"]')) {
  const s = document.createElement('script');
  s.type  = 'module';
  s.src   = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.4.0/model-viewer.min.js';
  document.head.appendChild(s);
}

export default function ResultsViewer({ jobId, onBack }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [tab,     setTab]     = useState('summary'); // 'summary' | '3d' | 'rules'

  useEffect(() => {
    if (!jobId) return;
    axios.get(`${API_BASE}/api/results/${jobId}`)
      .then(({ data }) => { setResults(data); setLoading(false); })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403 || status === 404 || !status) {
          setError('Results not available yet. The validation pipeline is not yet deployed.');
        } else {
          setError('Could not load results.');
        }
        setLoading(false);
      });
  }, [jobId]);

  if (loading) return <p style={styles.center}>Loading results…</p>;
  if (error)   return (
    <div style={styles.page}>
      <button onClick={onBack} style={styles.backBtn}>← Back to Jobs</button>
      <div style={{ textAlign: 'center', padding: '60px 24px', marginTop: 24 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
        <p style={{ color: '#888', fontSize: 14 }}>{error}</p>
      </div>
    </div>
  );
  if (!results) return null;

  const { summary, ruleResults, pdfUrl, modelUrl } = results;

  // Build chart data from rule categories
  const chartData = Object.entries(
    ruleResults.reduce((acc, r) => {
      acc[r.category] = acc[r.category] || { pass: 0, fail: 0 };
      r.passed ? acc[r.category].pass++ : acc[r.category].fail++;
      return acc;
    }, {})
  ).map(([name, v]) => ({ name, ...v }));

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>← Back to Jobs</button>
        <h2 style={{ margin: 0 }}>Results: <code style={{ fontSize: 16 }}>{jobId.slice(0, 8)}…</code></h2>
        <a href={pdfUrl} download style={styles.pdfBtn} onClick={e => { if (!pdfUrl) { e.preventDefault(); alert('PDF report not available for this job.'); } }}>⬇ Download PDF Report</a>
      </div>

      {/* Score Banner */}
      <div style={{ ...styles.banner, background: summary.overallPass ? '#eafaf1' : '#fdf2f2' }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: summary.overallPass ? '#27ae60' : '#c0392b' }}>
          {summary.overallPass ? '✅ PASSED' : '❌ FAILED'}
        </span>
        <span style={styles.stat}>{summary.passCount} / {summary.totalRules} rules passed</span>
        <span style={styles.stat}>Score: <strong>{summary.score}%</strong></span>
        <span style={styles.stat}>Processed: {new Date(summary.completedAt).toLocaleString()}</span>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {['summary', '3d', 'rules'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ ...styles.tab, borderBottom: tab === t ? '3px solid #0073bb' : '3px solid transparent' }}>
            {{ summary: '📊 Summary', '3d': '🧊 3D Preview', rules: '📋 Rule Details' }[t]}
          </button>
        ))}
      </div>

      {/* Tab: Summary Chart */}
      {tab === 'summary' && (
        <div style={styles.card}>
          <h3>Pass / Fail by Category</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="pass" name="Pass" fill="#27ae60" />
              <Bar dataKey="fail" name="Fail" fill="#c0392b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tab: 3D Preview */}
      {tab === '3d' && (
        <div style={styles.card}>
          <h3>3D Model Preview</h3>
          {modelUrl
            ? (
              /* eslint-disable-next-line */
              <model-viewer
                src={modelUrl}
                alt="BIW 3D model"
                camera-controls
                auto-rotate
                style={{ width: '100%', height: 480, background: '#1a1a2e', borderRadius: 8 }}
              />
            )
            : <p style={{ color: '#888' }}>3D preview not available for this job (GLB/FBX not generated).</p>
          }
        </div>
      )}

      {/* Tab: Rule Details */}
      {tab === 'rules' && (
        <div style={styles.card}>
          <h3>Rule-by-Rule Results</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Rule', 'Category', 'Status', 'Value', 'Threshold', 'Message'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ruleResults.map((r, i) => (
                <tr key={i} style={{ background: r.passed ? 'transparent' : '#fff5f5' }}>
                  <td style={styles.td}><strong>{r.ruleName}</strong></td>
                  <td style={styles.td}>{r.category}</td>
                  <td style={styles.td}>
                    <Cell fill={r.passed ? '#27ae60' : '#c0392b'} />
                    <span style={{ color: r.passed ? '#27ae60' : '#c0392b', fontWeight: 600 }}>
                      {r.passed ? '✅ PASS' : '❌ FAIL'}
                    </span>
                  </td>
                  <td style={styles.td}>{r.actualValue ?? '—'}</td>
                  <td style={styles.td}>{r.threshold ?? '—'}</td>
                  <td style={styles.td}>{r.message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  page:   { maxWidth: 960, margin: '0 auto', padding: 24, fontFamily: 'sans-serif' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  backBtn:{ padding: '6px 14px', background: '#eee', border: 'none', borderRadius: 4, cursor: 'pointer' },
  pdfBtn: { padding: '8px 16px', background: '#e67e22', color: '#fff', borderRadius: 6, textDecoration: 'none', fontWeight: 600 },
  banner: { display: 'flex', gap: 24, alignItems: 'center', padding: '16px 24px', borderRadius: 8, marginBottom: 16, flexWrap: 'wrap' },
  stat:   { fontSize: 14, color: '#555' },
  tabs:   { display: 'flex', gap: 0, borderBottom: '1px solid #ddd', marginBottom: 16 },
  tab:    { padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 },
  card:   { background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 20 },
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #ddd', background: '#f5f5f5', fontSize: 13 },
  td:     { padding: '8px 10px', borderBottom: '1px solid #eee', fontSize: 13 },
  center: { textAlign: 'center', marginTop: 40 },
};
